import { useEffect, useMemo, useState } from "react";
import PageShell from "../components/layout/PageShell";
import {
    getFilteredLeads,
    updateLeadStatus,
    updateLeadDetails,
    getLeadAudit,
    exportLeadsCsvUrl
} from "../services/crmService";
import toast from "react-hot-toast";

import "./leads.css";

const LEAD_STATUSES = ["New", "Contacted", "Won", "Lost"];

function getErrorMessage(error, fallback = "Something went wrong.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

function formatDateTime(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function normalizeStatus(value = "") {
    return String(value || "New").trim() || "New";
}

function getStatusClass(status = "") {
    const value = String(status).toLowerCase();

    if (value === "won") return "won";
    if (value === "lost") return "lost";
    if (value === "contacted") return "contacted";

    return "new";
}

function normalizeText(value) {
    return String(value || "").trim().toLowerCase();
}

function getLeadName(lead) {
    return lead?.fullName || lead?.name || "Unnamed Lead";
}

function getLeadContact(lead) {
    return lead?.email || lead?.phone || "-";
}

function getLeadInterest(lead) {
    return (
        lead?.programName ||
        lead?.itemTitle ||
        lead?.ctaLabel ||
        lead?.message ||
        "Website enquiry"
    );
}

function getLeadSourceContext(lead) {
    return [
        lead?.pageKey ? `Page: ${lead.pageKey}` : "",
        lead?.moduleKey ? `Module: ${lead.moduleKey}` : "",
        lead?.ctaLabel ? `CTA: ${lead.ctaLabel}` : "",
        lead?.variantKey ? `Variant: ${lead.variantKey}` : "",
        lead?.formType ? `Form: ${lead.formType}` : "",
        lead?.utmSource ? `UTM: ${lead.utmSource}` : ""
    ].filter(Boolean);
}

function LeadStatCard({ label, value, hint }) {
    return (
        <div className="lead-stat-card">
            <span>{label}</span>
            <strong>{value || 0}</strong>
            {hint && <small>{hint}</small>}
        </div>
    );
}

function LeadEmptyState({ hasFilters, onClear }) {
    return (
        <div className="lead-empty-state">
            <div className="lead-empty-icon">📩</div>
            <h3>{hasFilters ? "No leads match these filters" : "No leads yet"}</h3>
            <p>
                {hasFilters
                    ? "Try clearing filters or changing the status/source/search."
                    : "New enquiries from generated websites will appear here automatically."}
            </p>

            {hasFilters && (
                <button type="button" className="crm-btn crm-btn-primary" onClick={onClear}>
                    Clear Filters
                </button>
            )}
        </div>
    );
}

function LeadDetailModal({
    lead,
    auditItems,
    auditLoading,
    ownerName,
    notes,
    selectedLeadStatus,
    detailsSaving,
    updatingId,
    onClose,
    onOwnerChange,
    onNotesChange,
    onSaveDetails,
    onStatusChange
}) {
    if (!lead) return null;

    const sourceContext = getLeadSourceContext(lead);

    return (
        <div className="lead-modal-backdrop">
            <section className="lead-modal">
                <div className="lead-modal-header">
                    <div>
                        <span className="project-kicker">Lead Details</span>
                        <h2>{getLeadName(lead)}</h2>
                        <p>
                            Review enquiry details, source context, owner assignment, notes,
                            and activity history.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="lead-modal-close"
                        onClick={onClose}
                        aria-label="Close lead details"
                    >
                        ×
                    </button>
                </div>

                <div className="lead-modal-body">
                    <div className="lead-modal-grid">
                        <div className="lead-modal-main">
                            <div className="lead-detail-summary">
                                <div>
                                    <span>Status</span>
                                    <strong className={`lead-status-text ${getStatusClass(selectedLeadStatus)}`}>
                                        {selectedLeadStatus}
                                    </strong>
                                </div>

                                <div>
                                    <span>Created</span>
                                    <strong>{formatDateTime(lead.createdAtUtc)}</strong>
                                </div>

                                <div>
                                    <span>Source</span>
                                    <strong>{lead.source || "Website"}</strong>
                                </div>

                                <div>
                                    <span>Owner</span>
                                    <strong>{lead.ownerName || "Unassigned"}</strong>
                                </div>
                            </div>

                            <div className="lead-contact-card">
                                <span>Contact</span>
                                <strong>{lead.email || "-"}</strong>
                                <strong>{lead.phone || "-"}</strong>
                            </div>

                            <div className="lead-message-card">
                                <span>Interested In</span>
                                <strong>{getLeadInterest(lead)}</strong>
                                {lead.message ? <p>{lead.message}</p> : null}
                            </div>

                            <div className="lead-status-action-card">
                                <span>Update Status</span>

                                <div className="lead-status-action-grid">
                                    {LEAD_STATUSES.map((item) => (
                                        <button
                                            key={item}
                                            type="button"
                                            className={`lead-status-action ${normalizeStatus(lead.status) === item ? "active" : ""}`}
                                            disabled={updatingId === lead.id}
                                            onClick={() => onStatusChange(lead.id, item)}
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="lead-detail-form">
                                <label>
                                    <span>Owner Name</span>
                                    <input
                                        value={ownerName}
                                        onChange={(event) => onOwnerChange(event.target.value)}
                                        placeholder="Assign owner"
                                    />
                                </label>

                                <label>
                                    <span>Notes</span>
                                    <textarea
                                        value={notes}
                                        onChange={(event) => onNotesChange(event.target.value)}
                                        placeholder="Add follow-up notes, call summary, next action..."
                                        rows={5}
                                    />
                                </label>

                                <button
                                    type="button"
                                    className="project-action-btn primary full"
                                    onClick={onSaveDetails}
                                    disabled={detailsSaving}
                                >
                                    {detailsSaving ? "Saving..." : "Save Lead Details"}
                                </button>
                            </div>
                        </div>

                        <aside className="lead-modal-side">
                            <div className="lead-source-card">
                                <span>Lead Source Context</span>

                                {sourceContext.length === 0 ? (
                                    <p>No source context captured.</p>
                                ) : (
                                    <div className="lead-source-list">
                                        {sourceContext.map((item) => (
                                            <strong key={item}>{item}</strong>
                                        ))}
                                    </div>
                                )}

                                {lead?.itemTitle || lead?.formType ? (
                                    <p>
                                        {lead?.itemTitle ? `Item: ${lead.itemTitle}` : ""}
                                        {lead?.formType ? ` • Form: ${lead.formType}` : ""}
                                    </p>
                                ) : null}

                                {lead?.pageUrl && (
                                    <a
                                        href={lead.pageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="project-action-btn secondary full"
                                    >
                                        Open Source Page
                                    </a>
                                )}
                            </div>

                            <div className="lead-audit-section">
                                <div className="lead-audit-head">
                                    <strong>Activity History</strong>
                                    <span>{auditItems.length} update{auditItems.length === 1 ? "" : "s"}</span>
                                </div>

                                {auditLoading ? (
                                    <div className="lead-loading-state small">
                                        Loading activity...
                                    </div>
                                ) : auditItems.length === 0 ? (
                                    <div className="lead-mini-empty">
                                        No activity recorded yet.
                                    </div>
                                ) : (
                                    <div className="activity-list">
                                        {auditItems.map((item, index) => (
                                            <div className="activity-item" key={`${item.actionType}-${index}`}>
                                                <div className="activity-item-top">
                                                    <strong>{item.actionType}</strong>
                                                    <span>{item.changedBy || "System"}</span>
                                                </div>

                                                <div className="activity-item-sub">
                                                    {item.oldValue || "-"} → {item.newValue || "-"}
                                                </div>

                                                <div className="activity-item-date">
                                                    {formatDateTime(item.createdAtUtc)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default function LeadsPage() {
    const [leads, setLeads] = useState([]);
    const [source, setSource] = useState("");
    const [status, setStatus] = useState("");
    const [searchText, setSearchText] = useState("");
    const [updatingId, setUpdatingId] = useState("");
    const [selectedLeadId, setSelectedLeadId] = useState("");
    const [auditItems, setAuditItems] = useState([]);
    const [ownerName, setOwnerName] = useState("");
    const [notes, setNotes] = useState("");
    const [selectedLead, setSelectedLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailsSaving, setDetailsSaving] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);

    const hasFilters = Boolean(source || status || searchText);

    const stats = useMemo(() => {
        const total = leads.length;
        const won = leads.filter((x) => normalizeStatus(x.status) === "Won").length;
        const contacted = leads.filter((x) => normalizeStatus(x.status) === "Contacted").length;
        const newCount = leads.filter((x) => normalizeStatus(x.status) === "New").length;

        return {
            total,
            newCount,
            contacted,
            won
        };
    }, [leads]);

    const filteredLeads = useMemo(() => {
        const query = normalizeText(searchText);

        if (!query) return leads;

        return leads.filter((lead) => {
            const text = [
                lead.fullName,
                lead.email,
                lead.phone,
                lead.programName,
                lead.message,
                lead.source,
                lead.status,
                lead.ownerName,
                lead.pageKey,
                lead.moduleKey,
                lead.ctaLabel,
                lead.itemTitle,
                lead.formType
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return text.includes(query);
        });
    }, [leads, searchText]);

    const selectedLeadStatus = normalizeStatus(selectedLead?.status);

    const load = async (filters = {}) => {
        try {
            setLoading(true);

            const res = await getFilteredLeads(filters);
            const nextLeads = Array.isArray(res) ? res : [];

            setLeads(nextLeads);

            if (selectedLeadId) {
                const refreshedSelected = nextLeads.find((x) => x.id === selectedLeadId) || null;
                setSelectedLead(refreshedSelected);

                if (!refreshedSelected) {
                    setSelectedLeadId("");
                    setOwnerName("");
                    setNotes("");
                    setAuditItems([]);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to load leads."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentServerFilters = () => ({
        source: source || undefined,
        status: status || undefined
    });

    const handleApply = () => {
        load(currentServerFilters());
    };

    const handleClearFilters = () => {
        setSource("");
        setStatus("");
        setSearchText("");
        load();
    };

    const handleStatusChange = async (leadId, nextStatus) => {
        try {
            setUpdatingId(leadId);

            await updateLeadStatus(leadId, nextStatus);

            toast.success("Lead status updated.");

            setLeads((prev) =>
                prev.map((lead) =>
                    lead.id === leadId
                        ? { ...lead, status: nextStatus }
                        : lead
                )
            );

            if (selectedLeadId === leadId) {
                setSelectedLead((prev) =>
                    prev ? { ...prev, status: nextStatus } : prev
                );

                try {
                    const items = await getLeadAudit(leadId);
                    setAuditItems(items || []);
                } catch {
                    setAuditItems([]);
                }
            }

            await load(currentServerFilters());
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to update lead status."));
        } finally {
            setUpdatingId("");
        }
    };

    const handleOpenLead = async (lead) => {
        setSelectedLeadId(lead.id);
        setOwnerName(lead.ownerName || "");
        setSelectedLead(lead);
        setNotes(lead.notes || "");

        try {
            setAuditLoading(true);

            const items = await getLeadAudit(lead.id);
            setAuditItems(items || []);
        } catch {
            setAuditItems([]);
        } finally {
            setAuditLoading(false);
        }
    };

    const handleCloseLead = () => {
        setSelectedLeadId("");
        setSelectedLead(null);
        setOwnerName("");
        setNotes("");
        setAuditItems([]);
    };

    const handleSaveDetails = async () => {
        if (!selectedLeadId) return;

        try {
            setDetailsSaving(true);

            await updateLeadDetails(selectedLeadId, {
                ownerName,
                notes,
                changedBy: "Admin"
            });

            toast.success("Lead details updated.");

            setSelectedLead((prev) =>
                prev
                    ? {
                        ...prev,
                        ownerName,
                        notes
                    }
                    : prev
            );

            await load(currentServerFilters());

            const items = await getLeadAudit(selectedLeadId);
            setAuditItems(items || []);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to save lead details."));
        } finally {
            setDetailsSaving(false);
        }
    };

    return (
        <PageShell title="Leads">
            <div className="lead-page-shell">
                <section className="lead-command-card">
                    <div>
                        <span className="project-kicker">Lead Pipeline</span>
                        <h2>Customer enquiries from generated websites</h2>
                        <p>
                            Track new enquiries, assign owners, update status, review source context,
                            and manage follow-up notes from one full-width lead inbox.
                        </p>
                    </div>

                    <div className="lead-command-actions">
                        <a
                            className="project-action-btn secondary"
                            href={exportLeadsCsvUrl(source, status)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Export CSV
                        </a>

                        <button
                            type="button"
                            className="project-action-btn primary"
                            onClick={() => load(currentServerFilters())}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh Leads"}
                        </button>
                    </div>
                </section>

                <section className="lead-stats-grid">
                    <LeadStatCard label="Total Leads" value={stats.total} hint="Current view" />
                    <LeadStatCard label="New" value={stats.newCount} hint="Needs attention" />
                    <LeadStatCard label="Contacted" value={stats.contacted} hint="In progress" />
                    <LeadStatCard label="Won" value={stats.won} hint="Converted" />
                </section>

                <section className="lead-filter-card">
                    <div className="lead-filter-grid lead-filter-grid-wide">
                        <label>
                            <span>Search</span>
                            <input
                                placeholder="Search name, email, phone, program, source, CTA..."
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                            />
                        </label>

                        <label>
                            <span>Source</span>
                            <input
                                placeholder="generated-site, booking-modal..."
                                value={source}
                                onChange={(event) => setSource(event.target.value)}
                            />
                        </label>

                        <label>
                            <span>Status</span>
                            <select
                                value={status}
                                onChange={(event) => setStatus(event.target.value)}
                            >
                                <option value="">All statuses</option>
                                {LEAD_STATUSES.map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                        </label>

                        <div className="lead-filter-actions">
                            <button
                                type="button"
                                className="crm-btn crm-btn-primary"
                                onClick={handleApply}
                                disabled={loading}
                            >
                                Apply
                            </button>

                            <button
                                type="button"
                                className="crm-btn"
                                onClick={handleClearFilters}
                                disabled={loading || !hasFilters}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </section>

                <section className="lead-list-panel lead-list-panel-full">
                    <div className="lead-panel-header">
                        <div>
                            <span className="project-kicker">Inbox</span>
                            <h3>All Leads</h3>
                            <p>
                                Row click opens lead detail modal. Use status dropdown for quick updates.
                            </p>
                        </div>

                        <div className="lead-result-count">
                            <span>{filteredLeads.length}</span>
                            <strong>visible</strong>
                        </div>
                    </div>

                    {loading ? (
                        <div className="lead-loading-state">
                            Loading leads...
                        </div>
                    ) : filteredLeads.length === 0 ? (
                        <LeadEmptyState
                            hasFilters={hasFilters}
                            onClear={handleClearFilters}
                        />
                    ) : (
                        <>
                            <div className="lead-desktop-table lead-desktop-table-full">
                                <div className="variant-table-wrap">
                                    <table className="variant-table lead-table lead-ops-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Contact</th>
                                                <th>Interest</th>
                                                <th>Source</th>
                                                <th>Status</th>
                                                <th>Owner</th>
                                                <th>Date</th>
                                                <th>Update</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredLeads.map((lead) => (
                                                <tr
                                                    key={lead.id}
                                                    onClick={() => handleOpenLead(lead)}
                                                    className={`lead-row-clickable ${selectedLeadId === lead.id ? "selected" : ""}`}
                                                >
                                                    <td>
                                                        <strong>{getLeadName(lead)}</strong>
                                                        {lead.itemTitle ? <small>{lead.itemTitle}</small> : null}
                                                    </td>

                                                    <td>
                                                        <div className="lead-contact-cell">
                                                            <span>{lead.email || "-"}</span>
                                                            <small>{lead.phone || "-"}</small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="lead-interest-cell">
                                                            <strong>{getLeadInterest(lead)}</strong>
                                                            {lead.message ? <span>{lead.message}</span> : null}
                                                        </div>
                                                    </td>

                                                    <td>{lead.source || "-"}</td>

                                                    <td>
                                                        <span className={`lead-status-pill ${getStatusClass(lead.status)}`}>
                                                            {normalizeStatus(lead.status)}
                                                        </span>
                                                    </td>

                                                    <td>{lead.ownerName || "-"}</td>

                                                    <td>{formatDateTime(lead.createdAtUtc)}</td>

                                                    <td>
                                                        <select
                                                            className="crm-select"
                                                            value={normalizeStatus(lead.status)}
                                                            disabled={updatingId === lead.id}
                                                            onClick={(event) => event.stopPropagation()}
                                                            onChange={(event) => handleStatusChange(lead.id, event.target.value)}
                                                        >
                                                            {LEAD_STATUSES.map((item) => (
                                                                <option key={item} value={item}>{item}</option>
                                                            ))}
                                                        </select>
                                                    </td>

                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact secondary"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleOpenLead(lead);
                                                            }}
                                                        >
                                                            Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="lead-mobile-list">
                                {filteredLeads.map((lead) => (
                                    <button
                                        key={lead.id}
                                        type="button"
                                        className={`lead-mobile-card ${selectedLeadId === lead.id ? "selected" : ""}`}
                                        onClick={() => handleOpenLead(lead)}
                                    >
                                        <div className="lead-mobile-card-top">
                                            <strong>{getLeadName(lead)}</strong>
                                            <span className={`lead-status-pill ${getStatusClass(lead.status)}`}>
                                                {normalizeStatus(lead.status)}
                                            </span>
                                        </div>

                                        <p>{getLeadInterest(lead)}</p>

                                        <div className="lead-mobile-meta">
                                            <span>{lead.email || lead.phone || "No contact"}</span>
                                            <span>{lead.source || "Website"}</span>
                                            <span>{formatDateTime(lead.createdAtUtc)}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </section>

                <LeadDetailModal
                    lead={selectedLead}
                    auditItems={auditItems}
                    auditLoading={auditLoading}
                    ownerName={ownerName}
                    notes={notes}
                    selectedLeadStatus={selectedLeadStatus}
                    detailsSaving={detailsSaving}
                    updatingId={updatingId}
                    onClose={handleCloseLead}
                    onOwnerChange={setOwnerName}
                    onNotesChange={setNotes}
                    onSaveDetails={handleSaveDetails}
                    onStatusChange={handleStatusChange}
                />
            </div>
        </PageShell>
    );
}